import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import { supabase } from '../../lib/supabase';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtCurrency = (n) =>
  n != null ? `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '₹0';

const EARN_STATUSES = ['do_generated', 'completed'];

// ── Add Special Deal Modal ────────────────────────────────────────────────────
const AddDealModal = ({ agentId, buyers, onSaved, onClose }) => {
  const [buyerId, setBuyerId] = useState('');
  const [rate, setRate] = useState('');
  const [tonnage, setTonnage] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSave = async () => {
    if (!buyerId)                                 { setErr('Select a buyer.'); return; }
    if (!rate || isNaN(Number(rate)))             { setErr('Enter a valid rate.'); return; }
    if (!tonnage || Number(tonnage) <= 0)         { setErr('Enter valid tonnage cap.'); return; }
    setSaving(true); setErr('');
    const { error } = await supabase
      .from('agent_buyer_special_deals')
      .insert({
        agent_id: agentId,
        buyer_id: buyerId,
        special_rate_per_ton: Number(rate),
        total_tonnage_mt: Number(tonnage),
        notes: notes.trim() || null,
        status: 'active',
      });
    setSaving(false);
    if (error) { setErr('Save failed: ' + error.message); return; }
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 pb-4"
      style={{ background: 'rgba(0,0,0,0.5)' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-gray-900">Add Special Deal</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <Icon name="X" size={18} />
          </button>
        </div>
        {err && <div className="mb-3 p-2 rounded bg-red-50 text-xs text-red-700">{err}</div>}
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Buyer *</label>
            <select value={buyerId} onChange={e => { setBuyerId(e.target.value); setErr(''); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400">
              <option value="">Select buyer…</option>
              {buyers.map(b => (
                <option key={b.user_id} value={b.user_id}>
                  {b.company_name || b.full_name || b.mobile_phone}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Special Rate ₹/T *</label>
              <input type="number" value={rate} onChange={e => { setRate(e.target.value); setErr(''); }}
                placeholder="e.g. 15" min="0"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Tonnage Cap (MT) *</label>
              <input type="number" value={tonnage} onChange={e => { setTonnage(e.target.value); setErr(''); }}
                placeholder="e.g. 1000" min="1"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Notes</label>
            <input value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Special Oct 2026 deal"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
          </div>
          <button onClick={handleSave} disabled={saving}
            className="w-full py-2.5 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ background: '#7c3aed' }}>
            {saving ? 'Saving…' : 'Add Special Deal'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Special Deal Row ──────────────────────────────────────────────────────────
const SpecialDealRow = ({ deal, buyers, bookingsByBuyer, onCancel }) => {
  const [cancelling, setCancelling] = useState(false);
  const buyer = buyers.find(b => b.user_id === deal.buyer_id);
  const buyerName = buyer ? (buyer.company_name || buyer.full_name || '—') : '—';

  // Used tonnage = bookings for this buyer AFTER deal creation
  const usedMT = (bookingsByBuyer[deal.buyer_id] || [])
    .filter(b => new Date(b.created_at) >= new Date(deal.created_at))
    .reduce((s, b) => s + Number(b.quantity || 0), 0);

  const pct = Math.min(100, (usedMT / deal.total_tonnage_mt) * 100);
  const isExhausted = usedMT >= deal.total_tonnage_mt || deal.status === 'exhausted';
  const isCancelled = deal.status === 'cancelled';
  const statusColor = isCancelled ? '#9ca3af' : isExhausted ? '#059669' : '#7c3aed';
  const statusLabel = isCancelled ? 'Cancelled' : isExhausted ? 'Exhausted' : 'Active';

  const handleCancel = async () => {
    setCancelling(true);
    const { error } = await supabase
      .from('agent_buyer_special_deals')
      .update({ status: 'cancelled', updated_at: new Date().toISOString() })
      .eq('id', deal.id);
    setCancelling(false);
    if (!error) onCancel();
  };

  return (
    <div className="py-3 border-b border-purple-50 last:border-0">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-sm font-semibold text-gray-900 truncate">{buyerName}</p>
            <span className="text-xs font-bold px-1.5 py-0.5 rounded"
              style={{ background: statusColor + '20', color: statusColor }}>
              {statusLabel}
            </span>
          </div>
          <p className="text-xs text-gray-500 mb-1.5">
            ₹{deal.special_rate_per_ton}/T · {deal.total_tonnage_mt} MT cap
            {deal.notes && <span className="ml-1 text-gray-400">· {deal.notes}</span>}
          </p>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all"
                style={{ width: `${pct}%`, background: statusColor }} />
            </div>
            <span className="text-xs text-gray-500 flex-shrink-0">
              {usedMT.toFixed(1)} / {deal.total_tonnage_mt} MT
            </span>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-sm font-bold text-green-700">
            {fmtCurrency(usedMT * deal.special_rate_per_ton)}
          </p>
          <p className="text-xs text-gray-400">earned</p>
          {!isCancelled && !isExhausted && (
            <button onClick={handleCancel} disabled={cancelling}
              className="text-xs text-red-400 hover:text-red-600 mt-1 disabled:opacity-50">
              {cancelling ? '…' : 'Cancel'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Buyer Row ─────────────────────────────────────────────────────────────────
const BuyerRow = ({ buyer, bookings, onRateSaved }) => {
  const [rate, setRate] = useState(String(buyer.commission_rate_per_ton ?? 10));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');

  const totalMT = bookings.reduce((s, b) => s + Number(b.quantity || 0), 0);
  const earned = totalMT * Number(rate || 0);
  const bookingCount = bookings.length;
  const isDirty = String(rate) !== String(buyer.commission_rate_per_ton ?? 10);

  const handleSave = async () => {
    const parsed = parseFloat(rate);
    if (isNaN(parsed) || parsed < 0) { setErr('Enter a valid rate.'); return; }
    setSaving(true); setErr('');
    const { error } = await supabase
      .from('user_profiles')
      .update({ commission_rate_per_ton: parsed })
      .eq('user_id', buyer.user_id);
    setSaving(false);
    if (error) { setErr('Save failed.'); return; }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    if (onRateSaved) onRateSaved(buyer.user_id, parsed);
  };

  return (
    <div className="flex items-center gap-3 py-3 border-b border-gray-50 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">
          {buyer.company_name || buyer.full_name || '—'}
        </p>
        <p className="text-xs text-gray-400">{buyer.mobile_phone}</p>
      </div>
      <div className="text-right hidden sm:block w-24 flex-shrink-0">
        <p className="text-xs font-semibold text-gray-700">{totalMT.toFixed(1)} MT</p>
        <p className="text-xs text-gray-400">{bookingCount} booking{bookingCount !== 1 ? 's' : ''}</p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <span className="text-xs text-gray-500">₹</span>
        <input type="number" value={rate}
          onChange={e => { setRate(e.target.value); setErr(''); setSaved(false); }}
          className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-400"
          min="0" step="1" />
        <span className="text-xs text-gray-500">/T</span>
      </div>
      <div className="text-right flex-shrink-0 w-20">
        <p className="text-sm font-bold text-green-700">{fmtCurrency(earned)}</p>
        <p className="text-xs text-gray-400">normal</p>
      </div>
      <div className="flex-shrink-0 w-16 text-right">
        {err && <p className="text-xs text-red-500 mb-1">{err}</p>}
        {saved ? (
          <span className="text-xs text-green-600 font-semibold">✓ Saved</span>
        ) : (
          <button onClick={handleSave} disabled={saving || !isDirty}
            className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${
              isDirty ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-400 cursor-default'
            }`}>
            {saving ? '...' : 'Save'}
          </button>
        )}
      </div>
    </div>
  );
};

// ── Agent Card ────────────────────────────────────────────────────────────────
const AgentCard = ({ agent, buyers, bookingsByBuyer, deals, onDealsChanged }) => {
  const [open, setOpen] = useState(true);
  const [showSpecial, setShowSpecial] = useState(false);
  const [showAddDeal, setShowAddDeal] = useState(false);
  const [rates, setRates] = useState(
    Object.fromEntries(buyers.map(b => [b.user_id, b.commission_rate_per_ton ?? 10]))
  );

  const handleRateSaved = (buyerId, newRate) =>
    setRates(r => ({ ...r, [buyerId]: newRate }));

  const totalMT = buyers.reduce((s, b) =>
    s + (bookingsByBuyer[b.user_id] || []).reduce((q, bk) => q + Number(bk.quantity || 0), 0), 0);

  const normalEarned = buyers.reduce((sum, b) => {
    const mt = (bookingsByBuyer[b.user_id] || []).reduce((s, bk) => s + Number(bk.quantity || 0), 0);
    return sum + mt * (rates[b.user_id] ?? 10);
  }, 0);

  const specialEarned = deals.filter(d => d.status !== 'cancelled').reduce((sum, d) => {
    const usedMT = (bookingsByBuyer[d.buyer_id] || [])
      .filter(b => new Date(b.created_at) >= new Date(d.created_at))
      .reduce((s, b) => s + Number(b.quantity || 0), 0);
    return sum + usedMT * d.special_rate_per_ton;
  }, 0);

  const activeDeals = deals.filter(d => d.status === 'active');

  return (
    <>
      {showAddDeal && (
        <AddDealModal
          agentId={agent.user_id}
          buyers={buyers}
          onSaved={() => { setShowAddDeal(false); onDealsChanged(); }}
          onClose={() => setShowAddDeal(false)}
        />
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-4">
        {/* Agent header */}
        <button onClick={() => setOpen(o => !o)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Icon name="User" size={16} className="text-blue-700" />
            </div>
            <div className="text-left min-w-0">
              <p className="text-sm font-bold text-gray-900 truncate">
                {agent.company_name || agent.full_name || '—'}
              </p>
              <p className="text-xs text-gray-500">
                {buyers.length} buyer{buyers.length !== 1 ? 's' : ''} ·{' '}
                {totalMT.toFixed(1)} MT ·{' '}
                {fmtCurrency(normalEarned + specialEarned)} total accrued
                {activeDeals.length > 0 && (
                  <span className="ml-1 font-semibold" style={{ color: '#7c3aed' }}>
                    · {activeDeals.length} special deal{activeDeals.length !== 1 ? 's' : ''}
                  </span>
                )}
              </p>
            </div>
          </div>
          <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-gray-400 flex-shrink-0" />
        </button>

        {open && (
          <div>
            {/* ── Normal Commission Section ── */}
            <div className="px-4">
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Normal Commission</p>
                <p className="text-xs text-green-700 font-semibold">{fmtCurrency(normalEarned)} accrued</p>
              </div>

              {buyers.length === 0 ? (
                <p className="py-4 text-sm text-gray-400 text-center">No buyers registered yet.</p>
              ) : (
                <>
                  <div className="flex items-center gap-3 py-2 border-b border-gray-100">
                    <p className="flex-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">Buyer</p>
                    <p className="w-24 text-xs font-semibold text-gray-400 uppercase tracking-wide text-right hidden sm:block">Volume</p>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide w-24 text-center">Rate/Tonne</p>
                    <p className="w-20 text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">Earned</p>
                    <p className="w-16 text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">Action</p>
                  </div>
                  {buyers.map(b => (
                    <BuyerRow
                      key={b.user_id}
                      buyer={b}
                      bookings={bookingsByBuyer[b.user_id] || []}
                      onRateSaved={handleRateSaved}
                    />
                  ))}
                </>
              )}
            </div>

            {/* ── Special Deals Section ── */}
            <div className="px-4 border-t border-gray-100 mt-1">
              <div className="flex items-center justify-between py-2">
                <button onClick={() => setShowSpecial(s => !s)}
                  className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide"
                  style={{ color: '#7c3aed' }}>
                  <Icon name={showSpecial ? 'ChevronUp' : 'ChevronDown'} size={13} />
                  Special Deals
                  {deals.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold"
                      style={{ background: '#ede9fe', color: '#7c3aed' }}>
                      {deals.length}
                    </span>
                  )}
                </button>
                <div className="flex items-center gap-3">
                  {specialEarned > 0 && (
                    <p className="text-xs text-green-700 font-semibold">{fmtCurrency(specialEarned)} accrued</p>
                  )}
                  {buyers.length > 0 && (
                    <button onClick={() => setShowAddDeal(true)}
                      className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg transition-colors"
                      style={{ background: '#ede9fe', color: '#7c3aed' }}>
                      <Icon name="Plus" size={12} />
                      Add Deal
                    </button>
                  )}
                </div>
              </div>

              {showSpecial && (
                <div className="pb-3">
                  {deals.length === 0 ? (
                    <p className="py-3 text-sm text-gray-400 text-center">No special deals yet.</p>
                  ) : (
                    deals.map(d => (
                      <SpecialDealRow
                        key={d.id}
                        deal={d}
                        buyers={buyers}
                        bookingsByBuyer={bookingsByBuyer}
                        onCancel={onDealsChanged}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const AdminCommissionManagement = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agents, setAgents] = useState([]);
  const [buyersByAgent, setBuyersByAgent] = useState({});
  const [bookingsByBuyer, setBookingsByBuyer] = useState({});
  const [dealsByAgent, setDealsByAgent] = useState({});
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data: agentRows, error: agentErr } = await supabase
        .from('user_profiles')
        .select('user_id, full_name, company_name, email, mobile_phone')
        .or('role.eq.sales_agent,was_sales_agent.eq.true')
        .eq('is_active', true)
        .order('full_name');
      if (agentErr) throw agentErr;

      const agentList = agentRows || [];
      setAgents(agentList);
      if (agentList.length === 0) { setLoading(false); return; }

      const agentIds = agentList.map(a => a.user_id);

      // Buyers
      const { data: buyerRows, error: buyerErr } = await supabase
        .from('user_profiles')
        .select('user_id, full_name, company_name, mobile_phone, referred_by_agent_id, commission_rate_per_ton, created_at')
        .eq('role', 'buyer')
        .in('referred_by_agent_id', agentIds)
        .order('created_at');
      if (buyerErr) throw buyerErr;

      const buyerList = buyerRows || [];
      const grouped = {};
      agentIds.forEach(id => { grouped[id] = []; });
      buyerList.forEach(b => { if (grouped[b.referred_by_agent_id]) grouped[b.referred_by_agent_id].push(b); });
      setBuyersByAgent(grouped);

      // Bookings
      if (buyerList.length > 0) {
        const buyerIds = buyerList.map(b => b.user_id);
        const { data: bookingRows, error: bookErr } = await supabase
          .from('buyer_bookings')
          .select('readable_booking_id, buyer_id, quantity, status, created_at')
          .in('buyer_id', buyerIds)
          .in('status', EARN_STATUSES);
        if (bookErr) throw bookErr;

        const byBuyer = {};
        buyerIds.forEach(id => { byBuyer[id] = []; });
        (bookingRows || []).forEach(bk => { if (byBuyer[bk.buyer_id]) byBuyer[bk.buyer_id].push(bk); });
        setBookingsByBuyer(byBuyer);
      }

      // Special deals
      const { data: dealRows } = await supabase
        .from('agent_buyer_special_deals')
        .select('*')
        .in('agent_id', agentIds)
        .order('created_at', { ascending: false });

      const byAgent = {};
      agentIds.forEach(id => { byAgent[id] = []; });
      (dealRows || []).forEach(d => { if (byAgent[d.agent_id]) byAgent[d.agent_id].push(d); });
      setDealsByAgent(byAgent);

    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredAgents = agents.filter(a => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (a.full_name || '').toLowerCase().includes(q) ||
           (a.company_name || '').toLowerCase().includes(q);
  });

  const grandTotalMT = Object.values(bookingsByBuyer).flat()
    .reduce((s, b) => s + Number(b.quantity || 0), 0);

  const grandTotalEarned = agents.reduce((sum, agent) =>
    sum + (buyersByAgent[agent.user_id] || []).reduce((s, b) => {
      const mt = (bookingsByBuyer[b.user_id] || []).reduce((q, bk) => q + Number(bk.quantity || 0), 0);
      return s + mt * (b.commission_rate_per_ton ?? 10);
    }, 0), 0);

  const grandSpecialEarned = Object.values(dealsByAgent).flat()
    .filter(d => d.status !== 'cancelled')
    .reduce((sum, d) => {
      const usedMT = (bookingsByBuyer[d.buyer_id] || [])
        .filter(b => new Date(b.created_at) >= new Date(d.created_at))
        .reduce((s, b) => s + Number(b.quantity || 0), 0);
      return sum + usedMT * d.special_rate_per_ton;
    }, 0);

  return (
    <>
      <Helmet><title>Commission Management – SugarHathi Admin</title></Helmet>
      <RoleBasedHeader />

      <div className="min-h-screen bg-gray-50 pb-10">
        <div style={{ background: 'linear-gradient(135deg,#1e4a72,#15375a)' }} className="px-4 pt-5 pb-14">
          <p className="text-xs text-blue-300 mb-0.5">Admin · Sales</p>
          <h1 className="text-xl font-bold text-white">Commission Management</h1>
          <p className="text-xs text-blue-300 mt-0.5">Normal rates per buyer · Special deals with tonnage cap</p>
        </div>

        <div className="px-4 -mt-8">
          {/* Grand totals */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 text-center">
              <p className="text-xs text-gray-500">Agents</p>
              <p className="text-lg font-bold text-gray-900">{agents.length}</p>
            </div>
            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 text-center">
              <p className="text-xs text-gray-500">Total MT</p>
              <p className="text-lg font-bold text-gray-900">{grandTotalMT.toFixed(0)}</p>
            </div>
            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 text-center">
              <p className="text-xs text-gray-500">Normal</p>
              <p className="text-base font-bold text-green-700">{fmtCurrency(grandTotalEarned)}</p>
            </div>
            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 text-center">
              <p className="text-xs text-gray-500">Special</p>
              <p className="text-base font-bold" style={{ color: '#7c3aed' }}>{fmtCurrency(grandSpecialEarned)}</p>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search agent name..."
              className="w-full pl-8 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
                <p className="text-sm text-gray-400">Loading...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
              {error}
              <button onClick={loadData} className="ml-3 underline">Retry</button>
            </div>
          ) : agents.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center shadow-sm border border-gray-100">
              <Icon name="Users" size={32} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-500">No sales agents registered yet.</p>
            </div>
          ) : filteredAgents.length === 0 ? (
            <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-gray-100">
              <p className="text-sm text-gray-400">No agents match "{search}"</p>
            </div>
          ) : (
            filteredAgents.map(agent => (
              <AgentCard
                key={agent.user_id}
                agent={agent}
                buyers={buyersByAgent[agent.user_id] || []}
                bookingsByBuyer={bookingsByBuyer}
                deals={dealsByAgent[agent.user_id] || []}
                onDealsChanged={loadData}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default AdminCommissionManagement;
